import { createFileRoute, Navigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { SupportChatThread } from "@/components/support/SupportChatThread";
import { Loader2, LifeBuoy, Inbox } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useLocaleFormat } from "@/lib/i18n-format";

export const Route = createFileRoute("/_app/admin/support")({
  component: AdminSupportPage,
});

type ConversationRow = {
  id: string;
  company_id: string;
  subject: string | null;
  status: string;
  last_message_at: string | null;
  last_message_preview: string | null;
  last_message_role: string | null;
  unread_for_platform: number;
  companies: { name: string } | null;
};

function AdminSupportPage() {
  const { t } = useTranslation();
  const { formatDateTime } = useLocaleFormat();
  const { hasRole, loading } = useAuth();
  const isOwner = hasRole("owner");
  const [conversations, setConversations] = useState<ConversationRow[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loadingList, setLoadingList] = useState(true);

  useEffect(() => {
    if (!isOwner) return;
    let cancelled = false;
    async function load() {
      setLoadingList(true);
      const { data, error } = await supabase
        .from("support_conversations")
        .select(
          "id, company_id, subject, status, last_message_at, last_message_preview, last_message_role, unread_for_platform, companies(name)",
        )
        .order("last_message_at", { ascending: false, nullsFirst: false });
      if (cancelled) return;
      if (error) {
        toast.error(t("admin.support.loadError"), { description: error.message });
      } else {
        const rows = (data ?? []) as unknown as ConversationRow[];
        setConversations(rows);
        if (!selectedId && rows.length > 0) setSelectedId(rows[0].id);
      }
      setLoadingList(false);
    }
    load();

    const channel = supabase
      .channel("admin-support-list")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "support_conversations" },
        () => {
          void load();
        },
      )
      .subscribe();
    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [isOwner, selectedId, t]);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!isOwner) return <Navigate to="/dashboard" />;

  const selected = conversations.find((c) => c.id === selectedId) ?? null;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary">
          <LifeBuoy className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t("admin.support.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("admin.support.subtitle")}</p>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
        <div className="rounded-lg border border-border bg-card">
          <div className="flex items-center gap-2 border-b border-border px-3 py-2 text-sm font-medium">
            <Inbox className="h-4 w-4" />
            {t("admin.support.conversations")}
          </div>
          <div className="max-h-[70vh] overflow-y-auto">
            {loadingList ? (
              <div className="flex h-32 items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : conversations.length === 0 ? (
              <p className="p-4 text-sm text-muted-foreground">
                {t("admin.support.noConversations")}
              </p>
            ) : (
              conversations.map((c) => {
                const active = c.id === selectedId;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setSelectedId(c.id)}
                    className={cn(
                      "flex w-full flex-col items-start gap-1 border-b border-border px-3 py-2 text-left text-sm transition-colors",
                      active ? "bg-muted" : "hover:bg-muted/50",
                    )}
                  >
                    <div className="flex w-full items-center justify-between gap-2">
                      <span className="truncate font-medium">
                        {c.companies?.name ?? t("admin.support.fallbackCompany")}
                      </span>
                      {c.unread_for_platform > 0 && (
                        <span className="shrink-0 rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-bold text-primary-foreground">
                          {c.unread_for_platform}
                        </span>
                      )}
                    </div>
                    {c.last_message_preview && (
                      <p className="line-clamp-1 w-full text-xs text-muted-foreground">
                        {c.last_message_role === "platform" ? t("admin.support.youPrefix") : ""}
                        {c.last_message_preview}
                      </p>
                    )}
                    {c.last_message_at && (
                      <span className="text-[10px] text-muted-foreground">
                        {formatDateTime(c.last_message_at)}
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>

        <div>
          {selected ? (
            <SupportChatThread
              conversationId={selected.id}
              companyId={selected.company_id}
              viewerRole="platform"
            />
          ) : (
            <div className="flex h-[70vh] items-center justify-center rounded-lg border border-border text-sm text-muted-foreground">
              {t("admin.support.selectConversation")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
